import { Sort } from '@angular/material/sort';
import { functonLabel, IMachineState, IMachineFromApi, IMachine, IMachineEnergy } from '../../models';
import {
  Component,
  OnInit,
  Input,
  ViewEncapsulation,
  OnChanges,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { FeatureToggleService } from 'app/services';
import moment from 'moment';

@Component({
  selector: 'app-main-component',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
      transition(
        'expanded <=> void',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class MainComponent implements OnInit, OnChanges {
  @Input()
  machinesFromApi!: Array<IMachineFromApi>;

  @Input()
  machineStates!: Array<IMachineState>;

  @Input()
  machineEnergy!: Array<IMachineEnergy>;

  featurePowerEnabled = false;
  machines: Array<IMachine> = [];
  columnsToDisplay: string[] = [];
  columnsHeadLabels: Map<string, string> = new Map();
  expandedElement: IMachine | null = null;


  constructor(
    private readonly featureToggleService: FeatureToggleService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.updateMachinesState();
    this.featurePowerEnabled = (await this.featureToggleService.getFeatureToggle('FEAT_POWER')).enabled;
    this.columnsToDisplay = ['power', 'name', 'state', 'stateDescription', 'operation', '#']
      .filter(it => it !== 'power' || this.featurePowerEnabled);
    this.columnsHeadLabels = new Map(Object.entries(
      {
        power: 'Current power (watt)',
        state: 'Machine state',
        stateDescription: 'Current work',
        name: 'Machine name',
        operation: 'Machine operation'
      }
    ).filter(it => it[0] !== 'power' || this.featurePowerEnabled));

  }

  ngOnChanges(changes: any): void {
    if (changes.machinesFromApi) {
      this.machinesFromApi = changes.machinesFromApi.currentValue;
    }

    if (changes.machineStates) {
      this.machineStates = changes.machineStates.currentValue;
    }

    if (changes.machineEnergy) {
      this.machineEnergy = changes.machineEnergy.currentValue;
    }

    this.updateMachinesState();
  }

  sortData = (sort: Sort): void => {
    const data = this.machines.slice();
    if (!sort.active || sort.direction === '') {
      this.machines = data;
      return;
    }

    this.machines = data.sort((a: IMachine, b: IMachine) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'power':
          return this.compare(a.power, b.power, isAsc);
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'state':
          return this.compare(a.state, b.state, isAsc);
        case 'stateDescription':
          return this.compare(a.stateDescription, b.stateDescription, isAsc);
        case 'operation':
          return this.compare(a.operation, b.operation, isAsc);
        default:
          return 0;
      }
    });
  };

  private compare = (a: string = '', b: string = '', isAsc: boolean): number =>
    (a < b ? -1 : 1) * (isAsc ? 1 : -1);

  private updateMachinesState() {
    this.machines = this.machinesFromApi?.map(
      ({ id, name, function: operation }) => {
        const stateObj = this.machineStates.find(({ machineId }) => machineId === id);
        const energyDataForMachine = this.machineEnergy.find(({ machineId }) => machineId === id);
        const powerAlgorithm = energyDataForMachine?.machineActualMeasurementAlgorithm;
        const fiveMinutesAgo = moment(Date.now()).subtract(5, 'minutes');
        const isDataEnergyTimeisWithin5Minutes = energyDataForMachine && energyDataForMachine.dateTimeEnergy
          && moment(energyDataForMachine.dateTimeEnergy).isAfter(fiveMinutesAgo);
        const power = energyDataForMachine
          ? stateObj?.state === 'RUN' ||
            (powerAlgorithm === 'POLLING_DETECTION' && isDataEnergyTimeisWithin5Minutes)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ? energyDataForMachine!!.powerActive?.toString()
            : '-'
          : undefined;

        return {
          id,
          name,
          operation: functonLabel[operation],
          state: stateObj?.state,
          stateDescription: stateObj?.description,
          power,
          powerAlgorithm,
        };
      });
  }
}
